// Бакшишът в дневните отчети — САМО показване и сумиране на полетата от BE.
//
// BE смята всичко по сметка (от снимката на настройките при закриването ѝ,
// с исторически процент и режим на приспадане), закръгля по сметка и после
// сумира. Тук НЯМА формула: „За отчитане" е `amount_to_report` от BE, а
// сборът за период е сбор на тези стойности — формулата е линейна и BE вече е
// закръглил, затова сборът е точен. Пресмятане на „За отчитане" от сборовете
// на периода с текущия процент би било грешно (docs/reports.md §Бакшиш).
//
// Сумите се сумират в ЦЕЛИ центове (низът от BE → центове веднъж).
// Отрицателна стойност се пази такава — никакво ограничаване до 0
// (отрицателното „За отчитане" е „Доплащане към персонала").
//
// Модулът е в `lib/`, защото го ползват и хартиите в „Маркиране", и
// списъците в „Справки".

import type {
  PaymentBreakdownItem,
  ReportTipFields,
  TipOtherBreakdownRow,
  UserBreakdown,
} from "../api/reports";
import { formatCents, parseMoneyToCents } from "../utils/money";

/** Компонентите на бакшиша на отчет (или сбор от отчети) — в евро. */
export type ReportTips = {
  total: number;
  card: number;
  cardDeducted: number;
  cardSeparate: number;
  cardExpense: number;
  other: number;
  /** По метод извън картата; при сбор — слети по `payment_method_id`. */
  otherRows: TipOtherBreakdownRow[];
  /** „За отчитане" — `amount_to_report ?? cash_amount`, може да е < 0. */
  amountToReport: number;
  /** Поръчаното + бакшишите (сверка с бонове). */
  totalPaid: number;
  /** С карта общо (сверка с терминала). */
  cardPaid: number;
};

/** Полетата, които модулът чете от отчета (личен или на заведението). */
export type ReportWithTips = ReportTipFields & {
  total_amount: string;
  cash_amount: string;
  card_amount: string;
};

/** Центове на сума от BE; липсваща/невалидна → 0. */
const centsOf = (value: string | number | null | undefined): number =>
  parseMoneyToCents(value) ?? 0;

const euros = (cents: number): number => cents / 100;

/** Същата форма като `ReportTips`, но сумите са в ЦЕЛИ центове. */
type TipCents = ReportTips;

const tipCentsOf = (report: ReportWithTips): TipCents => ({
  total: centsOf(report.tip_total_amount),
  card: centsOf(report.tip_card_amount),
  cardDeducted: centsOf(report.tip_card_deducted_amount),
  cardSeparate: centsOf(report.tip_card_separate_amount),
  cardExpense: centsOf(report.tip_card_expense_amount),
  other: centsOf(report.tip_other_amount),
  otherRows: report.tip_other_breakdown ?? [],
  // По-старо BE без полето: „За отчитане" е в брой — вярно за всеки отчет без
  // бакшиш (а бакшиш може да има само при новото BE). НЕ е FE формула.
  amountToReport: centsOf(report.amount_to_report ?? report.cash_amount),
  totalPaid: centsOf(report.total_paid_amount ?? report.total_amount),
  cardPaid: centsOf(report.card_paid_amount ?? report.card_amount),
});

const toEuros = (cents: TipCents): ReportTips => ({
  total: euros(cents.total),
  card: euros(cents.card),
  cardDeducted: euros(cents.cardDeducted),
  cardSeparate: euros(cents.cardSeparate),
  cardExpense: euros(cents.cardExpense),
  other: euros(cents.other),
  otherRows: cents.otherRows,
  amountToReport: euros(cents.amountToReport),
  totalPaid: euros(cents.totalPaid),
  cardPaid: euros(cents.cardPaid),
});

/** Бакшишът на един отчет — точно стойностите от BE. */
export const reportTipsOf = (report: ReportWithTips): ReportTips =>
  toEuros(tipCentsOf(report));

/** Има ли отчетът (или сборът) бакшиш — гейтът на ЦЕЛИЯ бакшиш UI. */
export const hasReportTips = (tips: Pick<ReportTips, "total">): boolean =>
  Math.round(tips.total * 100) !== 0;

/** Сливане на редовете „друг метод" по id — сумата в центове. */
const mergeOtherRows = (
  rowsList: readonly TipOtherBreakdownRow[][],
): TipOtherBreakdownRow[] => {
  const byMethod = new Map<number, { row: TipOtherBreakdownRow; cents: number }>();
  for (const rows of rowsList) {
    for (const row of rows) {
      const existing = byMethod.get(row.payment_method_id);
      if (existing) {
        existing.cents += centsOf(row.amount);
        existing.row = {
          ...existing.row,
          bills_count: existing.row.bills_count + (row.bills_count ?? 0),
        };
      } else {
        byMethod.set(row.payment_method_id, { row: { ...row }, cents: centsOf(row.amount) });
      }
    }
  }
  return Array.from(byMethod.values()).map(({ row, cents }) => ({
    ...row,
    amount: formatCents(cents),
  }));
};

/**
 * Сборът за период (кутийките и редът „Общо" в „Справки"). Сумира отчетите
 * от ЕДИН вид — на заведението НЕ се събират с личните (те вече са вътре).
 */
export const sumReportTips = (
  reports: readonly ReportWithTips[],
): ReportTips => {
  const sum: TipCents = {
    total: 0,
    card: 0,
    cardDeducted: 0,
    cardSeparate: 0,
    cardExpense: 0,
    other: 0,
    otherRows: [],
    amountToReport: 0,
    totalPaid: 0,
    cardPaid: 0,
  };
  const otherRowsList: TipOtherBreakdownRow[][] = [];
  for (const report of reports) {
    const cents = tipCentsOf(report);
    sum.total += cents.total;
    sum.card += cents.card;
    sum.cardDeducted += cents.cardDeducted;
    sum.cardSeparate += cents.cardSeparate;
    sum.cardExpense += cents.cardExpense;
    sum.other += cents.other;
    sum.amountToReport += cents.amountToReport;
    sum.totalPaid += cents.totalPaid;
    sum.cardPaid += cents.cardPaid;
    otherRowsList.push(cents.otherRows);
  }
  sum.otherRows = mergeOtherRows(otherRowsList);
  return toEuros(sum);
};

/**
 * Стойността до „Сума за отчитане:" на хартията — низът от BE, а при по-старо
 * BE `cash_amount`. Отчет без бакшиш дава ТОЧНО досегашното число.
 */
export const amountToReportOf = (
  report: Pick<ReportWithTips, "amount_to_report" | "cash_amount">,
): string => report.amount_to_report ?? report.cash_amount;

/**
 * Доплащането към персонала: абсолютната стойност на отрицателното „За
 * отчитане"; `null`, когато няма доплащане.
 */
export const staffTopUpOf = (amountToReport: string | number): number | null => {
  const cents = centsOf(amountToReport);
  return cents < 0 ? euros(-cents) : null;
};

/**
 * „Бакшиш — друг метод": частта от `tip_other_amount`, която я няма в
 * редовете по метод (бакшиш без разпознаваем метод — нито в сметката, нито в
 * снимката). Същият остатък като реда на BE хартията
 * (`reports/print_payload._tips_block`), за да връзва прегледът със POS
 * печата; 0, когато редовете покриват сбора. Сметнато в центове.
 */
export const unlistedOtherTipOf = (
  tips: Pick<ReportTips, "other" | "otherRows">,
): number => {
  const listedCents = tips.otherRows.reduce((sum, row) => sum + centsOf(row.amount), 0);
  return euros(Math.round(tips.other * 100) - listedCents);
};

/** Името на метода в реда „друг метод" — EN при английски, иначе БГ. */
export const tipOtherMethodName = (
  row: TipOtherBreakdownRow,
  isEnglish: boolean,
): string => (isEnglish && row.name_en ? row.name_en : row.name_bg);

/** Ред за сверка „<метод> общо" под таблицата „Бакшиш". */
export type PaidByMethodRow = {
  paymentMethodId: number;
  nameBg: string;
  nameEn: string | null;
  /** Платеното с метода + бакшишът с него — в евро. */
  amount: number;
};

const CASH_PAYMENT_METHOD_ID = 1;
const CARD_PAYMENT_METHOD_ID = 2;
/** „Смесено" — в таблицата по видове плащания е разделено на брой и карта. */
const MIXED_PAYMENT_METHOD_ID = 3;

/**
 * Сверката по метод: платеното с всеки метод + бакшишът с него, само
 * ненулевите — в брой, с карта, после останалите в реда на таблицата по
 * видове плащания (и метод само с бакшиш — най-накрая). Брой и карта са
 * полетата на отчета (`cash_amount`; картата — `card_paid_amount` на BE,
 * сверката с терминала); другите — редът им в `payment_breakdown`. Към всеки
 * метод — неговият ред в `tip_other_breakdown` (при „В брой" — само бакшиш
 * от стара сметка). Сумирано в центове.
 */
export const paidByMethodOf = (
  report: ReportWithTips & { payment_breakdown?: PaymentBreakdownItem[] | null },
): PaidByMethodRow[] => {
  const breakdown = report.payment_breakdown ?? [];
  const nameOf = (methodId: number, fallbackBg: string, fallbackEn: string) => {
    const row = breakdown.find((item) => item.payment_method_id === methodId);
    return row
      ? { nameBg: row.name_bg, nameEn: row.name_en }
      : { nameBg: fallbackBg, nameEn: fallbackEn };
  };
  const tipOtherCents = new Map<number, number>();
  for (const row of report.tip_other_breakdown ?? []) {
    tipOtherCents.set(
      row.payment_method_id,
      (tipOtherCents.get(row.payment_method_id) ?? 0) + centsOf(row.amount),
    );
  }

  /** Бакшишът с метода от `tip_other_breakdown` — взима се веднъж. */
  const takeTipCents = (methodId: number): number => {
    const cents = tipOtherCents.get(methodId) ?? 0;
    tipOtherCents.delete(methodId);
    return cents;
  };

  const rowsInCents: { paymentMethodId: number; nameBg: string; nameEn: string | null; cents: number }[] = [
    {
      paymentMethodId: CASH_PAYMENT_METHOD_ID,
      ...nameOf(CASH_PAYMENT_METHOD_ID, "В брой", "Cash"),
      cents: centsOf(report.cash_amount) + takeTipCents(CASH_PAYMENT_METHOD_ID),
    },
    {
      paymentMethodId: CARD_PAYMENT_METHOD_ID,
      ...nameOf(CARD_PAYMENT_METHOD_ID, "С карта", "Card"),
      cents: centsOf(report.card_paid_amount ?? report.card_amount),
    },
  ];
  const isCashOrCard = (methodId: number) =>
    methodId === CASH_PAYMENT_METHOD_ID ||
    methodId === CARD_PAYMENT_METHOD_ID ||
    methodId === MIXED_PAYMENT_METHOD_ID;
  for (const item of breakdown) {
    if (isCashOrCard(item.payment_method_id)) continue;
    rowsInCents.push({
      paymentMethodId: item.payment_method_id,
      nameBg: item.name_bg,
      nameEn: item.name_en,
      cents: centsOf(item.amount) + takeTipCents(item.payment_method_id),
    });
  }
  // Метод само с бакшиш (липсва в таблицата по видове плащания).
  for (const row of report.tip_other_breakdown ?? []) {
    const cents = tipOtherCents.get(row.payment_method_id);
    if (cents === undefined || isCashOrCard(row.payment_method_id)) continue;
    rowsInCents.push({
      paymentMethodId: row.payment_method_id,
      nameBg: row.name_bg,
      nameEn: row.name_en,
      cents,
    });
    tipOtherCents.delete(row.payment_method_id);
  }
  return rowsInCents
    .filter((row) => row.cents !== 0)
    .map(({ cents, ...row }) => ({ ...row, amount: euros(cents) }));
};

/** Сервитьор с бакшиш в „Разбивка по потребители" — тогава таблицата получава
 *  „Бакшиш" и „За отчитане" (и прегледът на отчета на заведението е по-широк). */
export const hasUserTip = (user: Pick<UserBreakdown, "tip_total_amount">): boolean =>
  Math.round(Number(user.tip_total_amount ?? 0) * 100) !== 0;
